// aws-storage-operations.ts
import { Observable, from, throwError, Subject } from "rxjs";
import { map, catchError, filter, takeUntil, tap } from "rxjs/operators";
import { HttpClient, HttpEventType, HttpResponse } from "@angular/common/http";
import { IStorageOperations } from "./storage-operations.interface";
import { AwsCloudService } from "src/app/core/services/aws-cloud.service";
import { HeaderService } from "src/app/core/services/header.service";
import { ManageStorageLogsService } from "src/app/core/services/manage-storage-logs.service";
import {
    StorageItem,
    UploadProgress,
    OperationResult,
    FileOperationContext,
    DownloadResult,
    UploadContext,
    ProgressCallback
} from "src/app/core/models/storage-models.interface";

export class AwsStorageOperations implements IStorageOperations {

    constructor(
        private awsService: AwsCloudService,
        private headerService: HeaderService,
        private storageLogService: ManageStorageLogsService
    ) { }

    listFiles(path: string, context: FileOperationContext): Observable<StorageItem[]> {
        return this.awsService.getStorageData(context.deploymentName, context.currentStorageName, path)
            .pipe(
                map((data: any[]) => data.map(item => ({
                    name: item.name,
                    type: item.type === 'directory' ? 'directory' as const : 'file' as const,
                    path: context.defaultCurrentPath + `/${item.name}`,
                    owner: item.owner,
                    date: item.date ? new Date(item.date) : undefined,
                    size: item.size,
                    lastModified: item.date ? new Date(item.date) : undefined
                }))),
                catchError(error => throwError(() => new Error(`Failed to list AWS S3 objects: ${error.message}`)))
            );
    }

    uploadFile(file: File, destinationPath: string, context: FileOperationContext, uniqueId: number, uploadContext?: UploadContext): Observable<UploadProgress> {
        return new Observable<UploadProgress>(observer => {
            const fullPath = context.currentPath + "/" + file.name;
            const timeStarted = this.headerService.getUserNewDate();

            const cancelSubject = new Subject();
            this.storageLogService.onPremiseUploadRequests[uniqueId] = cancelSubject;

            this.awsService.uploadFilePresignedUrl(
                context.deploymentName,
                context.currentStorageName,
                fullPath
            ).subscribe({
                next: (url) => {
                    this.awsService.uploadObject(url, file)
                        .pipe(
                            filter((event) => !!event),
                            takeUntil(cancelSubject)
                        )
                        .subscribe({
                            next: (event: any) => {
                                if (event.type === HttpEventType.UploadProgress) {
                                    const percentage = Math.round((event.loaded / event.total) * 100);
                                    const timeElapsed = this.headerService.getUserNewDate().valueOf() - timeStarted.valueOf();
                                    const uploadSpeed = event.loaded / (timeElapsed / 1000);
                                    const timeRemaining = Math.round((file.size - event.loaded) / uploadSpeed);

                                    this.storageLogService.updateTimeRemaining(uniqueId, isFinite(timeRemaining) ? timeRemaining : 0);
                                    this.storageLogService.updateStatusOfLog(uniqueId, `Uploaded ${percentage}%`);
                                    this.storageLogService.updateStorageLogsStore();

                                    observer.next({
                                        loaded: event.loaded,
                                        total: event.total,
                                        percentage,
                                        timeRemaining: isFinite(timeRemaining) ? timeRemaining : undefined,
                                        uploadSpeed
                                    });
                                } else if (event instanceof HttpResponse) {
                                    this.storageLogService.updateTimeRemaining(uniqueId, null);
                                    this.storageLogService.updateRequestCompleteFields(uniqueId, "Upload Completed");

                                    observer.next({ loaded: file.size, total: file.size, percentage: 100 });
                                    observer.complete();
                                }
                            },
                            error: (error) => {
                                this.storageLogService.updateTimeRemaining(uniqueId, null);
                                this.storageLogService.updateRequestCompleteFields(uniqueId, "Upload Failed");
                                observer.error(error);
                            }
                        });
                },
                error: (error) => {
                    this.storageLogService.updateTimeRemaining(uniqueId, null);
                    this.storageLogService.updateRequestCompleteFields(uniqueId, "Upload Failed");
                    observer.error(error);
                }
            });
        });
    }

    downloadFile(filePath: string, context: FileOperationContext, uniqueId?: number): Observable<DownloadResult> {
        const fileName = filePath.split('/').pop() || '';

        if (uniqueId) {
            this.storageLogService.updateStatusOfLog(uniqueId, `Download started for ${fileName}`);
        }

        return new Observable<DownloadResult>(observer => {
            let cancelled = false;
            let statusUpdated = false;

            const cancellationMechanism = {
                abort: () => {
                    cancelled = true;

                    if (!statusUpdated && uniqueId) {
                        statusUpdated = true;
                        this.storageLogService.updateTimeRemaining(uniqueId, null);
                        this.storageLogService.updateRequestCompleteFields(uniqueId, 'Transfer Cancelled');
                    }
                },
                cancelSubject: new Subject<void>()
            };

            // Store in the service for external cancellation
            if (uniqueId) {
                this.storageLogService.awsDownloadRequests[uniqueId] = cancellationMechanism;
            }

            // Listen for external cancellation
            cancellationMechanism.cancelSubject.subscribe(() => {
                cancellationMechanism.abort();
            });

            const cleanup = () => {
                if (uniqueId && this.storageLogService.awsDownloadRequests[uniqueId]) {
                    delete this.storageLogService.awsDownloadRequests[uniqueId];
                }
                cancellationMechanism.cancelSubject.complete();
            };

            // Get download URL first
            this.awsService.downloadFile(context.deploymentName, context.currentStorageName, filePath)
                .subscribe({
                    next: (downloadUrl: string) => {
                        if (cancelled) {
                            cleanup();
                            observer.next({
                                blob: null,
                                directDownload: true,
                                fileName: fileName,
                                cancelled: true
                            } as any);
                            observer.complete();
                            return;
                        }

                        // Use native fetch for better cancellation control
                        const abortController = new AbortController();

                        // Connect our cancellation mechanism to AbortController
                        const originalAbort = cancellationMechanism.abort;
                        cancellationMechanism.abort = () => {
                            originalAbort();
                            abortController.abort();
                        };

                        fetch(downloadUrl, { signal: abortController.signal })
                            .then(response => {
                                if (cancelled) {
                                    cleanup();
                                    observer.next({
                                        blob: null,
                                        directDownload: true,
                                        fileName: fileName,
                                        cancelled: true
                                    } as any);
                                    observer.complete();
                                    return;
                                }

                                if (!response.ok) {
                                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                                }

                                const contentLength = response.headers.get('content-length');
                                const total = contentLength ? parseInt(contentLength, 10) : 0;

                                if (!response.body) {
                                    throw new Error('ReadableStream not supported');
                                }

                                const reader = response.body.getReader();
                                let receivedLength = 0;
                                const chunks: Uint8Array[] = [];
                                let lastProgress = 0;
                                const timeStarted = this.headerService.getUserNewDate();

                                const readChunk = (): Promise<void> => {
                                    return reader.read().then(({ done, value }) => {
                                        if (cancelled) {
                                            reader.cancel();
                                            cleanup();
                                            observer.next({
                                                blob: null,
                                                directDownload: true,
                                                fileName: fileName,
                                                cancelled: true
                                            } as any);
                                            observer.complete();
                                            return;
                                        }

                                        if (done) {
                                            if (!cancelled && !statusUpdated) {
                                                const blob = new Blob(chunks);
                                                statusUpdated = true;
                                                this.storageLogService.updateTimeRemaining(uniqueId, null);
                                                this.storageLogService.updateRequestCompleteFields(uniqueId, 'Transfer Completed');
                                                cleanup();
                                                observer.next({
                                                    blob: blob,
                                                    directDownload: true,
                                                    fileName: fileName
                                                });
                                                observer.complete();
                                            }
                                            return;
                                        }

                                        if (value && !cancelled) {
                                            chunks.push(value);
                                            receivedLength += value.length;

                                            if (total > 0) {
                                                const progress = Math.round((receivedLength / total) * 100);
                                                if (progress !== lastProgress && progress < 100) {
                                                    lastProgress = progress;

                                                    const timeElapsed = this.headerService.getUserNewDate().valueOf() - timeStarted.valueOf();
                                                    const downloadSpeed = receivedLength / (timeElapsed / 1000);
                                                    const timeRemaining = Math.round((total - receivedLength) / downloadSpeed);

                                                    this.storageLogService.updateTimeRemaining(uniqueId, isFinite(timeRemaining) ? timeRemaining : 0);
                                                    this.storageLogService.updateStatusOfLog(uniqueId, `Downloaded ${progress}%`);
                                                    this.storageLogService.updateStorageLogsStore();
                                                }
                                            }
                                        }

                                        if (!cancelled) {
                                            return readChunk();
                                        }
                                    });
                                };

                                return readChunk();
                            })
                            .catch(error => {
                                if (cancelled || error.name === 'AbortError') {
                                    if (!statusUpdated && uniqueId) {
                                        statusUpdated = true;
                                        this.storageLogService.updateTimeRemaining(uniqueId, null);
                                        this.storageLogService.updateRequestCompleteFields(uniqueId, 'Transfer Cancelled');
                                    }
                                    cleanup();
                                    observer.next({
                                        blob: null,
                                        directDownload: true,
                                        fileName: fileName,
                                        cancelled: true
                                    } as any);
                                    observer.complete();
                                } else {
                                    if (!statusUpdated && uniqueId) {
                                        statusUpdated = true;
                                        this.storageLogService.updateTimeRemaining(uniqueId, null);
                                        this.storageLogService.updateRequestCompleteFields(uniqueId, 'Transfer Failed');
                                    }
                                    cleanup();
                                    observer.error(error);
                                }
                            });
                    },
                    error: (error) => {
                        if (uniqueId) {
                            this.storageLogService.updateRequestCompleteFields(uniqueId, "Download Failed");
                        }
                        cleanup();
                        observer.error(error);
                    }
                });

            return cleanup;
        });
    }

    generateDownloadUrl(filePath: string, context: FileOperationContext): Observable<string> {
        const fileName = filePath.split('/').pop() || '';
        const fullPath = context.currentPath + "/" + fileName;
        
        return this.awsService.downloadFile(
            context.deploymentName,
            context.currentStorageName,
            fullPath
        ).pipe(
            catchError(error => throwError(() => new Error(`Failed to generate AWS download URL: ${error.message}`)))
        );
    }

    deleteFile(filePath: string, context: FileOperationContext, uniqueId?: number): Observable<OperationResult> {
        const fileName = filePath.split('/').pop() || '';
        const isDirectory = !fileName.includes('.');
        
        let deletePath = filePath;
        if (isDirectory) {
            deletePath = filePath.endsWith('/') ? filePath : `${filePath}/`;
        }
    
        return this.awsService.deleteObject(context.deploymentName, context.currentStorageName, deletePath)
            .pipe(
            map(() => ({ success: true, message: 'File deleted successfully' })),
            catchError(error => throwError(() => new Error(`AWS S3 delete failed: ${error.message}`)))
            );
    }

    createDirectory(directoryName: string, path: string, context: FileOperationContext, uniqueId?: number): Observable<OperationResult> {
        let processedDirName = directoryName;
        
        if (path !== ".") {
            processedDirName = path + "/" + directoryName;
        }
        
        return this.awsService.createFolder(
            context.deploymentName,
            processedDirName,
            context.currentStorageName
        ).pipe(
            map(() => ({ success: true, message: 'AWS S3 directory created successfully' })),
            tap(() => {
            if (uniqueId) {
                this.storageLogService.updateRequestCompleteFields(uniqueId, "Completed");
            }
            }),
            catchError(error => {
            if (uniqueId) {
                this.storageLogService.updateRequestCompleteFields(uniqueId, "Failed");
            }
            return throwError(() => new Error(`AWS S3 directory creation failed: ${error.message}`));
            })
        );
    }

    copyFile(sourcePath: string, destinationPath: string, context: FileOperationContext): Observable<OperationResult> {
        return throwError(() => new Error('Copy operation not implemented for AWS S3'));
    }

    renameFile(oldPath: string, newName: string, context: FileOperationContext): Observable<OperationResult> {
        return throwError(() => new Error('Rename operation not implemented for AWS S3'));
    }

    checkFileExists(filePath: string, context: FileOperationContext): Observable<boolean> {
        return this.listFiles(filePath.substring(0, filePath.lastIndexOf('/')), context)
            .pipe(
                map(files => {
                    const fileName = filePath.split('/').pop();
                    return files.some(file => file.name === fileName);
                }),
                catchError(() => throwError(() => false))
            );
    }

    readFileContent(filePath: string, context: FileOperationContext): Observable<string> {
        return this.awsService.viewFileData(context.deploymentName, context.currentStorageName, filePath)
            .pipe(
                catchError(error => throwError(() => new Error(`Failed to read AWS S3 file content: ${error.message}`)))
            );
    }

    saveFileContent(filePath: string, content: string | File, context: FileOperationContext): Observable<OperationResult> {
        if (content instanceof File) {
            return this.uploadFile(content, filePath, context, Date.now()).pipe(
                map(() => ({ success: true, message: 'File saved successfully' }))
            );
        } else {
            const fileName = filePath.split('/').pop() || 'file.txt';
            const file = new File([content], fileName, { type: 'text/plain' });

            return this.uploadFile(file, filePath, context, Date.now()).pipe(
                map(() => ({ success: true, message: 'File content saved successfully' }))
            );
        }
    }

    cancelOperation(uniqueId: number, operationType: 'upload' | 'download' | 'zip' | 'unzip' | 'general'): boolean {
        switch (operationType) {
            case 'upload':
                this.storageLogService.cancelAwsUploadRequest(uniqueId);
                return true;
            case 'download':
                this.storageLogService.cancelAwsDownloadRequest(uniqueId);
                return true;
            case 'zip':
            case 'unzip':
                this.storageLogService.updateRequestCompleteFields(uniqueId, `${operationType} not supported`);
                return false;
            default:
                this.storageLogService.updateRequestCompleteFields(uniqueId, "Operation Cancelled");
                return true;
        }
    }

    verifyDirectoryPath(paths: string[], context: FileOperationContext): Observable<boolean> {
        return new Observable<boolean>(observer => {
            const directoryPaths = paths.map(path => {
            const fullPath = context.currentPath === '.' ? path : `${context.currentPath}/${path}`;
            return fullPath;
            });

            Promise.all(
            directoryPaths.map(fullPath => {
                return this.awsService.createFolder(context.deploymentName, fullPath, context.currentStorageName).toPromise();
            })
            ).then(() => {
            observer.next(true);
            observer.complete();
            }).catch(error => {
            observer.next(true); 
            observer.complete();
            });
        });
    }

    viewFileData(filePath: string, context: FileOperationContext, uniqueId?: number): Observable<any> {
        return this.awsService.viewFileData(context.deploymentName, context.currentStorageName, filePath)
            .pipe(
            catchError(error => throwError(() => new Error(`Failed to view AWS S3 file: ${error.message}`)))
            );
    }
}